import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Edit, TrendingUp } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useGetEmployeesQuery } from "../employeesApi";
import { useGetDepartmentsQuery } from "../../departments/departmentsApi";
import { useGetHrDesignationsQuery } from "../../designations/hrDesignationsApi";
import { useCanCreateEmployees, useCanPromoteEmployees } from "../useEmployeePermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// Landing page for the employee master (Texas_Admin/GUI/Master/Frm_Employees.cs),
// against the live /api/v1/masters/employees endpoint (handoff §9.6).
// Strictly branch-scoped like every other branch-wise master - a
// branch-scoped (non all-branch) user only ever sees their own branch(es)
// here, same as Customers/Products; the header branch picker only actually
// does anything for an all-branch admin. Department/Designation/search
// filters run client-side - each branch's employee count is small (dozens,
// not thousands, unlike Customers/Products), so there's no need for the API
// to support them server-side.
const EmployeesList = () => {
  const canCreate = useCanCreateEmployees();
  const canPromote = useCanPromoteEmployees();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: employees = [], isLoading } = useGetEmployeesQuery({
    search: search || undefined,
    isActivate: status === "" ? undefined : status === "active",
    branchId: selectedBranchId ?? undefined,
  });
  const { data: departments = [] } = useGetDepartmentsQuery();
  const { data: designations = [] } = useGetHrDesignationsQuery();

  const visibleDesignations = departmentId
    ? designations.filter((d) => String(d.departmentId) === departmentId)
    : designations;

  const filtered = useMemo(
    () =>
      employees
        .filter((e) => !departmentId || String(e.departmentId) === departmentId)
        .filter((e) => !designationId || String(e.designationId) === designationId),
    [employees, departmentId, designationId]
  );

  const columns = [
    {
      title: "Code",
      dataIndex: "empCode",
      width: 90,
      render: (value, record) => <Link to={paths.employeeEdit(record.recordGuid)}>{value}</Link>,
    },
    {
      title: "Name",
      dataIndex: "empName",
      width: 220,
      ellipsis: { showTitle: true },
      render: (value, record) => `${value || ""} ${record.empLastName || ""}`.trim() || "-",
    },
    { title: "Department", dataIndex: "departmentName", render: (v) => v || "-" },
    { title: "Designation", dataIndex: "designationName", render: (v) => v || "-" },
    { title: "Mobile", dataIndex: "empMobile", render: (v) => v || "-" },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "Status",
      dataIndex: "isActivate",
      render: (value) => (
        <span className={`badge ${Number(value) ? "badge-linesuccess" : "badge-linedanger"}`}>
          {Number(value) ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="edit-delete-action">
          <button
            type="button"
            className="btn btn-link me-1 p-2"
            title="Edit"
            onClick={() => navigate(paths.employeeEdit(record.recordGuid))}
          >
            <Edit size={16} />
          </button>
          {canPromote && (
            <button
              type="button"
              className="btn btn-link p-2"
              title="Promote"
              onClick={() => navigate(paths.employeePromote(record.recordGuid))}
            >
              <TrendingUp size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Employees</h4>
              <h6>Employee master, salary details, and promotions</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.employeeNew)}>
                <PlusCircle className="me-2" size={16} />
                Add New Employee
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search employee name or code"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <select
                  className="form-control"
                  value={departmentId}
                  onChange={(e) => {
                    setDepartmentId(e.target.value);
                    setDesignationId("");
                  }}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.departmentCode} value={d.departmentCode}>{d.departmentName}</option>
                  ))}
                </select>
                <select
                  className="form-control"
                  value={designationId}
                  onChange={(e) => setDesignationId(e.target.value)}
                >
                  <option value="">All Designations</option>
                  {visibleDesignations.map((d) => (
                    <option key={d.designationCode} value={d.designationCode}>{d.designationName}</option>
                  ))}
                </select>
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filtered}
                rowKey="recordGuid"
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeesList;
